import * as THREE from "three";
import type { ChallengeKind } from "../challenge";
import { MARKER_R } from "../challenge";
import { HUB_R } from "../hubs";
import { pickupTexture } from "./textures";

// Free skate's challenge markers (#47): a glowing ring on the ground, a soft
// light column and a spinning "!" badge, colored by kind. A done challenge
// turns green with a check. The one the skater is standing in pulses.
// A challenge hub (#86) is the same marker, bigger, in magenta, with its tier
// number on the badge.

/** What a marker stands for: one challenge, or a tier's hub. */
export type MarkerKind = ChallengeKind | "hub";

/** A marker to draw. */
export interface MarkerSpot {
  id: string;
  kind: MarkerKind;
  x: number;
  z: number;
  /** The ground it stands on, when that's not the floor (a deck, #137). */
  y?: number;
  /** The badge's label while it's to do: "!" unless given (a hub's tier number). */
  label?: string;
}

const COLOR: Record<MarkerKind, string> = {
  score: "#ffd166",
  gap: "#ff8c42",
  collect: "#ef476f",
  trick: "#4cc9f0",
  combo: "#b388ff",
  targets: "#a3e635",
  graffiti: "#38bdf8",
  hub: "#f72585",
};
const DONE = "#06d6a0";
/** A challenge still locked on the ladder (#66): grey, with a padlock. */
const LOCKED = "#8d93a1";
export const MARKER_BEAM_H = 3.2;
/** The light column is thinner than the ring, so it glows without whiting out the view. */
const BEAM_R = 0.55;
export const MARKER_BADGE = 0.9;
/** How high a marker's badge floats (it bobs 0.1 m either way). */
export const MARKER_BADGE_Y = 2.6;

interface Marker {
  spot: MarkerSpot;
  root: THREE.Group;
  ring: THREE.Mesh<THREE.RingGeometry, THREE.MeshBasicMaterial>;
  beam: THREE.Mesh<THREE.CylinderGeometry, THREE.MeshBasicMaterial>;
  badge: THREE.Group;
  todo: THREE.Mesh[];
  check: THREE.Mesh[];
  lock: THREE.Mesh[];
}

export class MarkerMeshes {
  readonly root = new THREE.Group();
  private markers: Marker[] = [];
  private t = 0;
  private owned: (THREE.BufferGeometry | THREE.Material | THREE.Texture)[] = [];

  constructor(spots: readonly MarkerSpot[]) {
    const ringGeo = new THREE.RingGeometry(MARKER_R - 0.25, MARKER_R, 40);
    ringGeo.rotateX(-Math.PI / 2);
    const beamGeo = new THREE.CylinderGeometry(BEAM_R, BEAM_R, MARKER_BEAM_H, 24, 1, true);
    beamGeo.translate(0, MARKER_BEAM_H / 2, 0);
    const badgeGeo = new THREE.PlaneGeometry(MARKER_BADGE, MARKER_BADGE);
    const fade = beamFade();
    this.owned.push(ringGeo, beamGeo, badgeGeo);
    if (fade) this.owned.push(fade);
    const badgeMats = new Map<string, THREE.MeshBasicMaterial>();
    const badgeMat = (label: string, bg: string) => {
      const key = `${label}:${bg}`;
      let m = badgeMats.get(key);
      if (!m) {
        const map = pickupTexture(label, bg, "#ffffff");
        m = new THREE.MeshBasicMaterial({ map, color: map ? 0xffffff : bg, transparent: true, alphaTest: 0.5, toneMapped: false });
        badgeMats.set(key, m);
        this.owned.push(m);
        if (map) this.owned.push(map);
      }
      return m;
    };
    const twoSided = (mat: THREE.MeshBasicMaterial) => {
      const front = new THREE.Mesh(badgeGeo, mat);
      const back = new THREE.Mesh(badgeGeo, mat);
      back.rotation.y = Math.PI;
      return [front, back];
    };

    for (const spot of spots) {
      const root = new THREE.Group();
      root.position.set(spot.x, spot.y ?? 0, spot.z);
      if (spot.kind === "hub") root.scale.setScalar(HUB_R / MARKER_R);
      const ringMat = new THREE.MeshBasicMaterial({ color: COLOR[spot.kind], transparent: true, opacity: 0.9, depthWrite: false, toneMapped: false });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.position.y = 0.03;
      ring.renderOrder = 1;
      const beamMat = new THREE.MeshBasicMaterial({
        color: COLOR[spot.kind],
        alphaMap: fade,
        transparent: true,
        opacity: 0.22,
        side: THREE.DoubleSide,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        toneMapped: false,
      });
      const beam = new THREE.Mesh(beamGeo, beamMat);
      this.owned.push(ringMat, beamMat);
      const badge = new THREE.Group();
      badge.position.y = MARKER_BADGE_Y;
      const todo = twoSided(badgeMat(spot.label ?? "!", COLOR[spot.kind]));
      const check = twoSided(badgeMat("✓", DONE));
      const lock = twoSided(badgeMat("🔒", LOCKED));
      badge.add(...todo, ...check, ...lock);
      root.add(ring, beam, badge);
      this.root.add(root);
      this.markers.push({ spot, root, ring, beam, badge, todo, check, lock });
    }
  }

  /**
   * Spins the badges, shows done ones green and checked, locked ones grey
   * with a padlock, and pulses the marker the skater is in (`near`).
   */
  update(dt: number, done: (id: string) => boolean, near: string | null, locked: (id: string) => boolean = () => false): void {
    this.t += dt;
    for (const [i, m] of this.markers.entries()) {
      const isDone = done(m.spot.id);
      const isLocked = !isDone && locked(m.spot.id);
      const color = isDone ? DONE : isLocked ? LOCKED : COLOR[m.spot.kind];
      m.ring.material.color.set(color);
      m.beam.material.color.set(color);
      for (const o of m.todo) o.visible = !isDone && !isLocked;
      for (const o of m.check) o.visible = isDone;
      for (const o of m.lock) o.visible = isLocked;
      const pulse = m.spot.id === near ? 1 + Math.sin(this.t * 8) * 0.08 + 0.1 : 1;
      m.ring.scale.setScalar(pulse);
      m.beam.material.opacity = (m.spot.id === near ? 0.4 : 0.22) * (isDone ? 0.6 : 1);
      m.badge.rotation.y = this.t * 1.8 + i;
      m.badge.position.y = MARKER_BADGE_Y + Math.sin(this.t * 2.2 + i) * 0.1;
    }
  }

  dispose(): void {
    for (const r of this.owned) r.dispose();
  }
}

/** The light column's alpha: bright at the ground, gone at the top. */
function beamFade(): THREE.CanvasTexture | null {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  canvas.width = 4;
  canvas.height = 64;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  const g = ctx.createLinearGradient(0, 0, 0, 64);
  g.addColorStop(0, "#000");
  g.addColorStop(1, "#fff");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 4, 64);
  return new THREE.CanvasTexture(canvas);
}
